import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { de } from 'date-fns/locale';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import MetabaseDashboard from '~/components/metabase/MetabaseDashboard';
import OpenBillingDraftStatusBadge, { type OpenBillingDraftEntry } from '~/components/qm/OpenBillingDraftStatusBadge';
import { formatCurrency } from '~/lib/utils';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type BehandlungsdokuRow = {
    appointmentId: string;
    dateFrom: string;
    storeId: number | null;
    storeName: string;
    customerId: number;
    customerName: string;
    practitioners: string[];
    documentationStatus: 'NONE' | 'ATTRIBUTION_ONLY';
};

const DocumentationStatusBadge = ({ status }: { status: BehandlungsdokuRow['documentationStatus'] }) => {
    if (status === 'NONE') {
        return (
            <Badge variant='outline' className='bg-red-100 text-red-800 border-red-200'>
                Keine Dokumentation
            </Badge>
        );
    }
    return (
        <Badge variant='outline' className='bg-amber-100 text-amber-800 border-amber-200'>
            Nur Leistung dokumentiert
        </Badge>
    );
};

const useQmStoreFilter = () => {
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;
    const [storeFilter, setStoreFilter] = useState<string>(isAdmin ? 'all' : '');

    const { data: storesData } = trpc.stores.getAll.useQuery({ page: 0, pageSize: 200, filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data ?? [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && storeFilter === '') {
            setStoreFilter('all');
        } else if (!isAdmin && stores.length > 0 && !storeFilter) {
            setStoreFilter(String(stores[0].id));
        }
    }, [stores, isAdmin, storeFilter]);

    const storeId = storeFilter !== 'all' && storeFilter !== ''
        ? parseInt(storeFilter, 10)
        : undefined;

    return {
        isAdmin,
        stores,
        storeFilter,
        setStoreFilter,
        storeId,
        isStoreReady: isAdmin || storeId !== undefined,
    };
};

const StoreFilterSelect = ({
    isAdmin,
    stores,
    storeFilter,
    onStoreFilterChange,
}: {
    isAdmin: boolean;
    stores: { id: number; name: string }[];
    storeFilter: string;
    onStoreFilterChange: (value: string) => void;
}) => (
    <Card className='mb-6'>
        <CardContent className='pt-6'>
            <div className='flex flex-wrap gap-4'>
                <div className='min-w-[200px]'>
                    <Label className='text-xs text-muted-foreground mb-1 block'>Filiale</Label>
                    <Select value={storeFilter || undefined} onValueChange={onStoreFilterChange}>
                        <SelectTrigger>
                            <SelectValue placeholder='Filiale' />
                        </SelectTrigger>
                        <SelectContent>
                            {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                            {stores.map((store) => (
                                <SelectItem key={store.id} value={store.id.toString()}>
                                    {store.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </CardContent>
    </Card>
);

const BehandlungsdokuTab = () => {
    const navigate = useNavigate();
    const { isAdmin, stores, storeFilter, setStoreFilter, storeId, isStoreReady } = useQmStoreFilter();

    const { data, isLoading } = trpc.qm.getBehandlungsdokuEntries.useQuery({
        storeId,
    }, {
        enabled: isStoreReady,
    });

    const rows = (data?.entries as BehandlungsdokuRow[] | undefined) ?? [];

    const columns = useMemo<ColumnDef<BehandlungsdokuRow>[]>(() => [
        {
            id: 'dateFrom',
            header: 'Termin',
            cell: ({ row }) => (
                <span className='text-sm whitespace-nowrap'>
                    {format(new Date(row.original.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => (
                <span
                    className='font-medium cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customer/${row.original.customerId}`);
                    }}
                >
                    {row.original.customerName}
                </span>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => (
                <span className='text-sm'>{row.original.storeName}</span>
            ),
        },
        {
            id: 'practitioners',
            header: 'Behandler',
            cell: ({ row }) => (
                <span className='text-sm'>
                    {row.original.practitioners.length > 0 ? row.original.practitioners.join(', ') : '-'}
                </span>
            ),
        },
        {
            id: 'documentationStatus',
            header: 'Dokumentationsstatus',
            cell: ({ row }) => <DocumentationStatusBadge status={row.original.documentationStatus} />,
        },
    ], [navigate]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div>
            <StoreFilterSelect
                isAdmin={isAdmin}
                stores={stores}
                storeFilter={storeFilter}
                onStoreFilterChange={setStoreFilter}
            />

            <Card>
                <div className='rounded-md border'>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {columns.map((_, j) => (
                                            <TableCell key={j}>
                                                <Skeleton className='h-4 w-full' />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : table.getRowModel().rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                        Keine offenen Behandlungsdokumentationen gefunden
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

type LeistungsabgleichRow = {
    appointmentId: string;
    dateFrom: string;
    storeId: number | null;
    storeName: string;
    customerId: number;
    customerName: string;
    practitioners: string[];
    documentedVariants: string[];
    billedVariants: string[];
};

const VariantBadges = ({ variants, className }: { variants: string[]; className: string }) => {
    if (variants.length === 0) return <span className='text-sm text-muted-foreground'>-</span>;
    return (
        <div className='flex flex-wrap gap-1'>
            {variants.map((name, i) => (
                <Badge key={i} variant='outline' className={className}>
                    {name}
                </Badge>
            ))}
        </div>
    );
};

const LeistungsabgleichTab = () => {
    const navigate = useNavigate();
    const { isAdmin, stores, storeFilter, setStoreFilter, storeId, isStoreReady } = useQmStoreFilter();

    const { data, isLoading } = trpc.qm.getLeistungsabgleichEntries.useQuery({
        storeId,
    }, {
        enabled: isStoreReady,
    });

    const rows = (data?.entries as LeistungsabgleichRow[] | undefined) ?? [];

    const columns = useMemo<ColumnDef<LeistungsabgleichRow>[]>(() => [
        {
            id: 'dateFrom',
            header: 'Termin',
            cell: ({ row }) => (
                <span className='text-sm whitespace-nowrap'>
                    {format(new Date(row.original.dateFrom), 'dd.MM.yyyy HH:mm', { locale: de })}
                </span>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => (
                <span
                    className='font-medium cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customer/${row.original.customerId}`);
                    }}
                >
                    {row.original.customerName}
                </span>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => (
                <span className='text-sm'>{row.original.storeName}</span>
            ),
        },
        {
            id: 'practitioners',
            header: 'Behandler',
            cell: ({ row }) => (
                <span className='text-sm'>
                    {row.original.practitioners.length > 0 ? row.original.practitioners.join(', ') : '-'}
                </span>
            ),
        },
        {
            id: 'documentedVariants',
            header: 'Dokumentiert',
            cell: ({ row }) => (
                <VariantBadges
                    variants={row.original.documentedVariants}
                    className='bg-red-100 text-red-800 border-red-200'
                />
            ),
        },
        {
            id: 'billedVariants',
            header: 'Abgerechnet',
            cell: ({ row }) => (
                <VariantBadges
                    variants={row.original.billedVariants}
                    className='bg-amber-100 text-amber-800 border-amber-200'
                />
            ),
        },
    ], [navigate]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div>
            <StoreFilterSelect
                isAdmin={isAdmin}
                stores={stores}
                storeFilter={storeFilter}
                onStoreFilterChange={setStoreFilter}
            />

            <Card>
                <div className='rounded-md border'>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {columns.map((_, j) => (
                                            <TableCell key={j}>
                                                <Skeleton className='h-4 w-full' />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : table.getRowModel().rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                        Keine Abweichungen gefunden
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

const OffeneAuftraegeTab = () => {
    const navigate = useNavigate();
    const { isAdmin, stores, storeFilter, setStoreFilter, storeId, isStoreReady } = useQmStoreFilter();

    const { data, isLoading } = trpc.qm.getOffeneAuftraegeEntries.useQuery({
        storeId,
    }, {
        enabled: isStoreReady,
    });

    const rows = (data?.entries as OpenBillingDraftEntry[] | undefined) ?? [];

    const columns = useMemo<ColumnDef<OpenBillingDraftEntry>[]>(() => [
        {
            id: 'billingDraftDate',
            header: 'Datum',
            cell: ({ row }) => (
                <span className='text-sm whitespace-nowrap'>
                    {format(new Date(row.original.billingDraftDate), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            id: 'billingDraftNumber',
            header: 'Auftrag',
            cell: ({ row }) => (
                <span className='text-sm font-medium'>{row.original.billingDraftNumber}</span>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => (
                <span
                    className='font-medium cursor-pointer hover:underline text-primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/customer/${row.original.customerId}`);
                    }}
                >
                    {row.original.customerName}
                </span>
            ),
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => (
                <span className='text-sm'>{row.original.storeName}</span>
            ),
        },
        {
            id: 'total',
            header: 'Betrag',
            cell: ({ row }) => (
                <span className='text-sm whitespace-nowrap'>{formatCurrency(row.original.totalCents / 100)}</span>
            ),
        },
        {
            id: 'paid',
            header: 'Bezahlt',
            cell: ({ row }) => (
                <span className='text-sm whitespace-nowrap'>{formatCurrency(row.original.paidCents / 100)}</span>
            ),
        },
        {
            id: 'paymentStatus',
            header: 'Status',
            cell: ({ row }) => <OpenBillingDraftStatusBadge status={row.original.paymentStatus} />,
        },
    ], [navigate]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div>
            <StoreFilterSelect
                isAdmin={isAdmin}
                stores={stores}
                storeFilter={storeFilter}
                onStoreFilterChange={setStoreFilter}
            />

            <Card>
                <div className='rounded-md border'>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        {columns.map((_, j) => (
                                            <TableCell key={j}>
                                                <Skeleton className='h-4 w-full' />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : table.getRowModel().rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className='text-center py-8 text-muted-foreground'>
                                        Keine offenen Aufträge gefunden
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        className='cursor-pointer'
                                        onClick={() => navigate(`/billing-draft/${row.original.billingDraftId}`)}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
};

const UebersichtTab = () => {
    const { data, isLoading, error } = trpc.qm.getOverviewDashboardToken.useQuery(undefined, {
        refetchInterval: 8 * 60 * 1000,
        retry: false,
    });

    if (isLoading) {
        return <Skeleton className='h-[600px] w-full' />;
    }

    if (error) {
        return (
            <Alert variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertTitle>Dashboard nicht verfügbar</AlertTitle>
                <AlertDescription>{error.message}</AlertDescription>
            </Alert>
        );
    }

    if (!data) return null;

    return (
        <MetabaseDashboard
            token={data.token}
            instanceUrl={data.instanceUrl}
            withTitle
            withDownloads
        />
    );
};

const QM = () => {
    const [activeTab, setActiveTab] = useState('behandlungsdoku');

    return (
        <Content>
            <div className='container mx-auto py-10'>
                <div className='flex justify-between items-center mb-6'>
                    <h1 className='text-2xl font-bold'>QM</h1>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value='behandlungsdoku'>Behandlungsdoku</TabsTrigger>
                        <TabsTrigger value='leistungsabgleich'>Leistungsabgleich</TabsTrigger>
                        <TabsTrigger value='offene-auftraege'>Offene Aufträge</TabsTrigger>
                        <TabsTrigger value='uebersicht'>Übersicht</TabsTrigger>
                    </TabsList>
                    <TabsContent value='behandlungsdoku'>
                        <BehandlungsdokuTab />
                    </TabsContent>
                    <TabsContent value='leistungsabgleich'>
                        <LeistungsabgleichTab />
                    </TabsContent>
                    <TabsContent value='offene-auftraege'>
                        <OffeneAuftraegeTab />
                    </TabsContent>
                    <TabsContent value='uebersicht'>
                        <UebersichtTab />
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default QM;
